import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config, { PRICING, calculateGstComponents } from '../../../config';
import { generateReceiptPDF } from '../utils/receiptPdf';
import { useAuth } from '../../../context/AuthContext';

const apiBaseUrl = config.getApiUrl();

const emptyReceiptForm = () => ({
  amount: '',
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  receiptDate: new Date().toISOString().split('T')[0],
  planName: '',
  paymentId: '',
  subscriptionId: '',
  receiptNo: ''
});

export const useReceipts = (showSnackbar) => {
  const { user } = useAuth();
  const canFetchReceipts = Array.isArray(user?.tabs) && user.tabs.includes('receipts');
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(true);
  const [receiptsError, setReceiptsError] = useState(null);
  const [receiptsPage, setReceiptsPage] = useState(0);
  const [receiptsRowsPerPage, setReceiptsRowsPerPage] = useState(10);
  const [receiptForm, setReceiptForm] = useState(emptyReceiptForm);
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);

  const fetchReceipts = async () => {
    try {
      setReceiptsLoading(true);
      const response = await fetchWithAuth(`${apiBaseUrl}/api/receipts`);
      setReceipts(response.data || response || []);
    } catch (err) {
      console.error('Error fetching receipts:', err);
      setReceiptsError(err.message);
    } finally {
      setReceiptsLoading(false);
    }
  };

  useEffect(() => {
    if (!canFetchReceipts) {
      setReceiptsLoading(false);
      return;
    }
    fetchReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetchReceipts]);

  const handleReceiptsChangePage = (event, newPage) => {
    setReceiptsPage(newPage);
  };

  const handleReceiptsChangeRowsPerPage = (event) => {
    setReceiptsRowsPerPage(parseInt(event.target.value, 10));
    setReceiptsPage(0);
  };

  const handleReceiptFormChange = (field) => (event) => {
    setReceiptForm((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleGenerateReceipt = async () => {
    try {
      setIsGeneratingReceipt(true);

      // Validate required fields
      if (!receiptForm.amount) {
        showSnackbar('Amount is required', 'error');
        return;
      }

      if (!receiptForm.customerEmail) {
        showSnackbar('Customer email is required', 'error');
        return;
      }

      const amount = parseFloat(receiptForm.amount);
      if (isNaN(amount) || amount <= 0) {
        showSnackbar('Please enter a valid amount', 'error');
        return;
      }

      // Calculate GST using the same logic as in the application
      const gstComponents = calculateGstComponents(amount);

      // Prepare receipt data
      const receiptData = {
        subscriptionId: receiptForm.subscriptionId || null,
        paymentId: receiptForm.paymentId || null,
        customerName: receiptForm.customerName || 'N/A',
        customerEmail: receiptForm.customerEmail,
        customerPhone: receiptForm.customerPhone || null,
        planName: receiptForm.planName || 'Custom Plan',
        amount: gstComponents.totalAmount,
        baseAmount: gstComponents.baseAmount,
        gstAmount: gstComponents.gstAmount,
        gstRate: PRICING.GST.RATE,
        gstNumber: PRICING.GST.NUMBER,
        receiptDate: new Date(receiptForm.receiptDate).toISOString(),
        receiptNo: receiptForm.receiptNo || null
      };

      // Save receipt to database
      const response = await fetchWithAuth(`${apiBaseUrl}/api/receipts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(receiptData)
      });

      if (response.success) {
        showSnackbar('Receipt generated and saved successfully!', 'success');

        // Reset form
        setReceiptForm(emptyReceiptForm());

        // Refresh receipts list
        if (canFetchReceipts) {
          fetchReceipts();
        }

        // Generate and download PDF
        generateReceiptPDF({
          ...receiptData,
          _id: response.receiptId || 'manual_' + Date.now()
        });
      }
    } catch (err) {
      console.error('Error generating receipt:', err);
      showSnackbar('Failed to generate receipt. Please try again.', 'error');
    } finally {
      setIsGeneratingReceipt(false);
    }
  };

  return {
    receipts,
    receiptsLoading,
    receiptsError,
    receiptsPage,
    receiptsRowsPerPage,
    receiptForm,
    isGeneratingReceipt,
    fetchReceipts,
    handleReceiptsChangePage,
    handleReceiptsChangeRowsPerPage,
    handleReceiptFormChange,
    handleGenerateReceipt
  };
};
